import React from "react";
import { useAuth } from "shared/context/auth";
import { ParticipantRole } from "shared/enums/ParticipantRole";
import { useGetProgramParticipantForOrgAdmin } from "./dataloader";
import { ManageParticipantProps, ManageParticipantService } from "./types";
import { useParticipantDetails } from "apps/myExperience/context/participantDetails";
import { ProgramType, TradeProgramsList } from "shared/enums/ProgramType";
import useLogAnalyticsScreenHook from "apps/myExperience/context/analyticsWrapper/hooks/useLogAnalyticsScreenHook";
import { AnalyticsScreenNameEnum } from "shared/enums/AnalyticsEnums";

export default function useManageParticipantService(props: ManageParticipantProps): ManageParticipantService {
  const { userDetail } = useAuth();
  const { getProgramDetails } = useParticipantDetails();
  useLogAnalyticsScreenHook({
    properties: {
      screenType: AnalyticsScreenNameEnum.MANAGE_PARTICIPANTS,
      programParticipantId: props.programParticipantId
    }
  });
  const isOrgAdmin = React.useMemo(
    () => userDetail?.participantRoles.includes(ParticipantRole.ORGANIZATION_ADMIN),
    [userDetail?.participantRoles]
  );

  const { data: programParticipantsManagedByOrgAdmin, refetch } = useGetProgramParticipantForOrgAdmin({
    orgAdminProgramParticipantId: isOrgAdmin ? props.programParticipantId : undefined
  });

  /**
   * For Trade program do not show purchase meeting cap message.
   * Also we need to show different tooltip message.
   * */
  const isTradeProgram = React.useMemo(() => {
    const program = props.programParticipantId ? getProgramDetails(props.programParticipantId) : null;
    return TradeProgramsList.includes(program?.programType as ProgramType);
  }, [getProgramDetails, props.programParticipantId]);

  const displayBuyMeetingCapMessage = React.useMemo(() => {
    return (
      !isTradeProgram &&
      programParticipantsManagedByOrgAdmin?.meetingCapInitialized === false &&
      programParticipantsManagedByOrgAdmin?.meetingRepTagAddAllowedAsPerProgramState === true
    );
  }, [
    isTradeProgram,
    programParticipantsManagedByOrgAdmin?.meetingCapInitialized,
    programParticipantsManagedByOrgAdmin?.meetingRepTagAddAllowedAsPerProgramState
  ]);

  const manageChangeHandler = React.useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    isTradeProgram,
    manageChangeHandler,
    displayBuyMeetingCapMessage,
    programParticipantsManagedByOrgAdmin
  };
}
